import {PlusOutlined} from '@ant-design/icons';
import {Flex, Spin} from 'antd';
import {RcFile} from 'antd/es/upload';
import {ChangeEvent, useState} from 'react';
import {v4 as uuidv4} from 'uuid';

import {Text} from '../../../../component/text/text';
import {Locale} from '../../../../provider/locale/localization';
import {useSnackbar} from '../../../../provider/snackbar/snackbar-hook';
import {uploadCustomReviewMedia} from '../../../../service/reviews/reviews-custom-media';
import {ApiError} from '../../../../util/error';

import {AttachmentTile} from './review-form-attachment-tile';
import {ReviewAttachmentType, ReviewAttachmentTypeEnum} from './review-form-type';
import * as styles from './review-form.scss';

const IMAGE_MAX_FILE_SIZE = 5 * 1024 * 1024;
const VIDEO_MAX_FILE_SIZE = 10 * 1024 * 1024;
const VIDEO_EXTENSIONS_REGEXP = /\.(mp4|webm|ogg|mov|m4v|avi|mkv)$/i;
const IMAGE_EXTENSIONS_REGEXP = /\.(jpg|jpeg|png|gif|webp)$/i;

type PropsType = {
    brandId: number | null;
    value?: Array<ReviewAttachmentType>;
    onChange?: (attachments: Array<ReviewAttachmentType>) => void;
};

function mapApiMediaType(type: 'image' | 'video'): ReviewAttachmentTypeEnum {
    return type === 'video' ? ReviewAttachmentTypeEnum.video : ReviewAttachmentTypeEnum.image;
}

function getFileType(file: RcFile): ReviewAttachmentTypeEnum | null {
    if (file.type.startsWith('image/') || IMAGE_EXTENSIONS_REGEXP.test(file.name)) {
        return ReviewAttachmentTypeEnum.image;
    }

    if (file.type.startsWith('video/') || VIDEO_EXTENSIONS_REGEXP.test(file.name)) {
        return ReviewAttachmentTypeEnum.video;
    }

    return null;
}

function getApiErrorMessage(error: unknown): string | null {
    if (!(error instanceof ApiError)) {
        return null;
    }

    if (typeof error.jsonData === 'object' && error.jsonData !== null) {
        const firstValue = Object.values(error.jsonData)[0];

        if (typeof firstValue === 'string') {
            return firstValue;
        }

        if (Array.isArray(firstValue) && typeof firstValue[0] === 'string') {
            return firstValue[0];
        }
    }

    return null;
}

export function ReviewFormAttachments(props: PropsType): JSX.Element {
    const {brandId, value = [], onChange} = props;
    const {snackbar} = useSnackbar();
    const [isUploading, setIsUploading] = useState(false);

    function removeAttachmentById(attachmentId: string) {
        const attachment = value.find((item) => item.id === attachmentId);
        const hasBlobPreview = attachment?.previewSrc?.startsWith('blob:');

        if (hasBlobPreview && attachment?.previewSrc) {
            URL.revokeObjectURL(attachment.previewSrc);
        }

        onChange?.(value.filter((item) => item.id !== attachmentId));
    }

    async function uploadFile(file: RcFile): Promise<ReviewAttachmentType | null> {
        const fileType = getFileType(file);

        if (!fileType) {
            snackbar.error(<Locale stringKey="REVIEW__FORM__FIELD_ATTACHMENTS__FILE_FORMAT_ERROR" />);
            return null;
        }

        if (fileType === ReviewAttachmentTypeEnum.image && file.size > IMAGE_MAX_FILE_SIZE) {
            snackbar.error(<Locale stringKey="REVIEW__FORM__FIELD_ATTACHMENTS__FILE_SIZE_IMAGE_ERROR" />);
            return null;
        }

        if (fileType === ReviewAttachmentTypeEnum.video && file.size > VIDEO_MAX_FILE_SIZE) {
            snackbar.error(<Locale stringKey="REVIEW__FORM__FIELD_ATTACHMENTS__FILE_SIZE_VIDEO_ERROR" />);
            return null;
        }

        if (!brandId) {
            snackbar.error(<Locale stringKey="REVIEW__FORM__FIELD_BRAND__PLACEHOLDER" />);
            return null;
        }

        try {
            const uploadedMedia = await uploadCustomReviewMedia(file, brandId);
            const attachmentType = mapApiMediaType(uploadedMedia.type);

            return {
                id: uuidv4(),
                type: attachmentType,
                src: uploadedMedia.url,
                previewSrc:
                    attachmentType === ReviewAttachmentTypeEnum.video ? URL.createObjectURL(file) : uploadedMedia.url,
            };
        } catch (error: unknown) {
            const apiErrorMessage = getApiErrorMessage(error);

            snackbar.error(apiErrorMessage || <Locale stringKey="REVIEW__FORM__FIELD_ATTACHMENTS__UPLOAD_ERROR" />);

            return null;
        }
    }

    async function onFileChange(event: ChangeEvent<HTMLInputElement>) {
        const {currentTarget} = event;
        const files = [...(currentTarget.files || [])] as Array<RcFile>;

        if (files.length === 0) {
            return;
        }

        setIsUploading(true);

        const results = await Promise.all(files.map(uploadFile));
        const uploadedAttachments = results.filter((item) => item !== null).filter(Boolean);

        if (uploadedAttachments.length > 0) {
            onChange?.([...value, ...uploadedAttachments]);
        }

        setIsUploading(false);
        currentTarget.value = '';
    }

    return (
        <Flex className={styles.ReviewForm_attachmentGrid} gap={8} wrap>
            <label>
                <input
                    accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,video/mp4,video/webm,video/ogg,video/quicktime,.mov,.m4v,.avi,.mkv"
                    className={styles.ReviewForm_uploadInput}
                    multiple
                    onChange={onFileChange}
                    type="file"
                />
                <div className={styles.ReviewForm_uploadTile}>
                    {isUploading ? (
                        <Spin className={styles.ReviewForm_uploadTileSpinner} size="small" />
                    ) : (
                        <div className={styles.ReviewForm_uploadTileBody}>
                            <PlusOutlined />
                            <Text
                                block
                                className={styles.ReviewForm_uploadTileText}
                                stringKey="REVIEW__FORM__FIELD_ATTACHMENTS__UPLOAD_BUTTON"
                            />
                        </div>
                    )}
                </div>
            </label>

            {value.map((attachment) => (
                <AttachmentTile
                    attachment={attachment}
                    key={attachment.id}
                    onDelete={() => removeAttachmentById(attachment.id)}
                />
            ))}
        </Flex>
    );
}
